/**
 * Media Bounding Box Component
 * 
 * Shows a green border with corner dots around the selected media item
 * Follows the media as it moves/scales
 * Corner dots can be dragged to resize the media
 */

import React, { useState, useEffect } from 'react';
import { MediaItem } from '../../types';

interface MediaBoundingBoxProps {
  isActive: boolean;
  selectedMediaItem: MediaItem | null;
  canvasDimensions: { width: number; height: number };
  sourceAspectRatio: string;
  onUpdateScale?: (itemId: string, scaleDelta: number) => void;
}

const MediaBoundingBox: React.FC<MediaBoundingBoxProps> = ({
  isActive,
  selectedMediaItem,
  canvasDimensions,
  sourceAspectRatio,
  onUpdateScale
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStartPos, setResizeStartPos] = useState({ x: 0, y: 0 });
  const [resizeStartScale, setResizeStartScale] = useState(1);
  const [resizeCorner, setResizeCorner] = useState<string>(''); // Track which corner

  if (!isActive || !selectedMediaItem || !canvasDimensions.width || !canvasDimensions.height) {
    return null;
  }

  // Parse source aspect ratio to get media dimensions
  const [sourceW, sourceH] = sourceAspectRatio.split(':').map(Number);
  const sourceRatio = sourceW / sourceH;

  // Calculate base media dimensions (fitting within canvas)
  const { width: canvasWidth, height: canvasHeight } = canvasDimensions;
  let baseWidth: number;
  let baseHeight: number;

  const canvasRatio = canvasWidth / canvasHeight;
  if (sourceRatio > canvasRatio) {
    // Media is wider - fit to width
    baseWidth = canvasWidth;
    baseHeight = canvasWidth / sourceRatio;
  } else {
    // Media is taller - fit to height
    baseHeight = canvasHeight;
    baseWidth = canvasHeight * sourceRatio;
  }

  // Get position and scale from media item
  const position = selectedMediaItem.position || { x: 0, y: 0 };
  const scale = selectedMediaItem.scale || { scaleX: 1, scaleY: 1 };

  // CRITICAL: Apply the SAME transform as the media
  // This makes the bounding box follow the media exactly
  const transform = `translate(${position.x}%, ${position.y}%) scale(${scale.scaleX}, ${scale.scaleY})`;
  
  // Box starts centered in canvas (same as media)
  const boxLeft = (canvasWidth - baseWidth) / 2;
  const boxTop = (canvasHeight - baseHeight) / 2;

  // Corner dot style
  const cornerStyle = (corner: string) => {
    // Determine cursor based on corner
    let cursor = 'nwse-resize'; // Default for nw and se
    if (corner === 'top-right' || corner === 'bottom-left') {
      cursor = 'nesw-resize'; // Opposite diagonal
    }
    
    return {
      position: 'absolute' as const,
      width: '20px',
      height: '20px',
      background: '#10b981',
      border: '3px solid white',
      borderRadius: '50%',
      cursor: cursor,
      zIndex: 20,
      boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
      ...(corner.includes('top') ? { top: '-10px' } : { bottom: '-10px' }),
      ...(corner.includes('left') ? { left: '-10px' } : { right: '-10px' })
    };
  };

  // Handle corner dot mouse down (start resize)
  const handleCornerMouseDown = (e: React.MouseEvent, corner: string) => {
    e.stopPropagation();
    e.preventDefault();
    
    setIsResizing(true);
    setResizeCorner(corner); // Store which corner
    setResizeStartPos({ x: e.clientX, y: e.clientY });
    setResizeStartScale(scale.scaleX); // Store current scale
  };

  // Global resize handlers
  useEffect(() => {
    if (!isResizing || !onUpdateScale || !selectedMediaItem || !resizeCorner) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Calculate distance moved from start
      const deltaX = e.clientX - resizeStartPos.x;
      const deltaY = e.clientY - resizeStartPos.y;
      
      // Determine direction multiplier based on corner
      // For left corners: moving left = grow (negative deltaX = positive scale)
      // For right corners: moving right = grow (positive deltaX = positive scale)
      // For top corners: moving up = grow (negative deltaY = positive scale)
      // For bottom corners: moving down = grow (positive deltaY = positive scale)
      
      let directionX = 1;
      let directionY = 1;
      
      if (resizeCorner.includes('left')) {
        directionX = -1; // Invert for left corners
      }
      if (resizeCorner.includes('top')) {
        directionY = -1; // Invert for top corners
      }
      
      // Apply direction to deltas
      const adjustedDeltaX = deltaX * directionX;
      const adjustedDeltaY = deltaY * directionY;
      
      // Use average of X and Y movement for uniform scaling
      const avgDelta = (adjustedDeltaX + adjustedDeltaY) / 2;
      
      // Convert pixel movement to scale change (adjust sensitivity)
      const scaleSensitivity = 0.003; // Adjust this for faster/slower resize
      const scaleChange = avgDelta * scaleSensitivity;
      
      // Calculate new scale (relative to start scale)
      const newScale = resizeStartScale + scaleChange;
      
      // Apply limits (min 0.1x, max 3x)
      const clampedScale = Math.max(0.1, Math.min(3, newScale));
      
      // Calculate delta from current scale
      const currentScale = selectedMediaItem.scale?.scaleX || 1;
      const scaleDelta = clampedScale - currentScale;
      
      // Only update if there's actual change
      if (Math.abs(scaleDelta) > 0.001) {
        onUpdateScale(selectedMediaItem.id!, scaleDelta);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeCorner('');
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeStartPos, resizeStartScale, resizeCorner, onUpdateScale, selectedMediaItem]);

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none', // Let mouse events pass through to media
        zIndex: 15
      }}
    >
      {/* Media bounding box - uses SAME transform as media */}
      <div
        style={{
          position: 'absolute',
          left: `${boxLeft}px`,
          top: `${boxTop}px`,
          width: `${baseWidth}px`,
          height: `${baseHeight}px`,
          border: '3px solid #10b981',
          boxShadow: '0 0 0 1px rgba(16, 185, 129, 0.5)',
          pointerEvents: 'none', // Don't block mouse events
          transform: transform, // SAME transform as media!
          transformOrigin: 'center center'
        }}
      >
        {/* Corner resize handles */}
        {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map((corner) => (
          <div
            key={corner}
            style={{...cornerStyle(corner), pointerEvents: 'auto'}} // Only dots are interactive
            onMouseDown={(e) => handleCornerMouseDown(e, corner)}
          />
        ))}

        {/* Media label */}
        <div
          style={{
            position: 'absolute',
            top: '-30px',
            left: '0',
            background: '#10b981',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            pointerEvents: 'none'
          }}
        >
          {selectedMediaItem.name || 'Media Item'}
        </div>
      </div>
    </div>
  );
};

export default MediaBoundingBox;
